// Community photos are mostly hotlinked Pexels/Unsplash URLs, often with no
// size params — Pexels then serves the full original (a 4000×6000, 600 KB+
// file for a hero that's ~400px wide on a phone). Both CDNs resize on the
// fly from query params, so ask for roughly the size we'll actually draw.
// Other hosts can't be resized and are returned unchanged.
//
// Keep in sync with sizedImageUrl in scripts/generate-og-pages.js — the
// static HTML preloads the hero with the same URLs, so they must match
// exactly or the browser downloads the hero twice.

export const HERO_WIDTHS = [800, 1200, 1600, 2000];

export const isResizableImage = (url) =>
  /^https:\/\/images\.pexels\.com\//.test(url || '') ||
  /^https:\/\/(images|plus)\.unsplash\.com\//.test(url || '');

export const sizedImageUrl = (url, width) => {
  if (!isResizableImage(url)) return url;
  try {
    const u = new URL(url);
    if (u.hostname === 'images.pexels.com') {
      u.searchParams.delete('h');
      u.searchParams.delete('dpr');
      u.searchParams.set('auto', 'compress');
      u.searchParams.set('cs', 'tinysrgb');
      u.searchParams.set('w', String(width));
    } else {
      u.searchParams.delete('h');
      u.searchParams.set('w', String(width));
      u.searchParams.set('q', '75');
      u.searchParams.set('auto', 'format');
      if (!u.searchParams.has('fit')) u.searchParams.set('fit', 'crop');
    }
    return u.toString();
  } catch {
    return url;
  }
};

// For full-width <img>s — pair with sizes="100vw" so the browser picks.
export const heroSrcSet = (url) =>
  isResizableImage(url) ? HERO_WIDTHS.map(w => `${sizedImageUrl(url, w)} ${w}w`).join(', ') : undefined;

// CSS backgrounds can't use srcset, so pick the smallest width that covers
// the viewport at its pixel density (capped at 2x — sharper is invisible).
export const viewportImageWidth = () => {
  const needed = (window.innerWidth || 1200) * Math.min(window.devicePixelRatio || 1, 2);
  return HERO_WIDTHS.find(w => w >= needed) || HERO_WIDTHS[HERO_WIDTHS.length - 1];
};

// Carousels only need the slide on screen plus its neighbours (the one
// fading out and the one coming next) — not every slide up front.
export const isNearSlide = (i, active, count) =>
  count <= 3 || i === active || i === (active + 1) % count || i === (active - 1 + count) % count;
